import { useEffect, useState } from "react";
import api from "@/lib/api";
import AppLayout from "@/components/AppLayout";
import Disclaimer from "@/components/Disclaimer";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine } from "recharts";
import { Target, Scale, UtensilsCrossed, Plus, X, TrendingDown, TrendingUp, Loader2, Check, Sparkles, Apple, Minus, ChefHat, Heart, Trash2, ChevronDown } from "lucide-react";

const SLOTS = [
  { key: "Breakfast", emoji: "🌅" },
  { key: "Lunch", emoji: "☀️" },
  { key: "Dinner", emoji: "🌙" },
  { key: "Snacks", emoji: "🍎" },
];

// Curated BloomHer meal ideas for the Menu Builder quick-add
const MEAL_IDEAS = {
  Breakfast: ["Greek yogurt & berries", "Veggie egg scramble", "Overnight oats & chia", "Protein smoothie", "Avocado toast & egg"],
  Lunch: ["Grilled chicken salad", "Salmon & quinoa bowl", "Lentil & veggie soup", "Turkey lettuce wraps", "Chickpea Mediterranean bowl"],
  Dinner: ["Baked salmon & greens", "Stir-fry veggies & tofu", "Lean beef & roasted veg", "Zucchini noodle pasta", "Herb chicken & sweet potato"],
  Snacks: ["Baked apple with stevia", "Naked peach with cinnamon", "Hummus & cucumber", "Cottage cheese & berries", "Cantaloupe slices"],
};

const RAINBOW = [
  { key: "red", label: "Red", hex: "#C0392B" },
  { key: "orange", label: "Orange", hex: "#E67E22" },
  { key: "yellow", label: "Yellow", hex: "#F1C40F" },
  { key: "green", label: "Green", hex: "#4A6B53" },
  { key: "blue", label: "Blue", hex: "#3B6FA0" },
  { key: "purple", label: "Purple", hex: "#7D5BA6" },
  { key: "white", label: "White", hex: "#F4EFE9" },
];
const FOOD_GROUPS = ["Protein", "Vegetables", "Fruits"];
const SUPPLEMENTS = ["B12", "Vitamin D", "Magnesium"];

const FOOD_LOG_GROUPS = [
  { name: "Proteins", emoji: "🍗", group: "Protein", items: ["Chicken", "Fish", "Egg", "Turkey", "Beef"] },
  { name: "Fruits", emoji: "🍓", group: "Fruits", items: ["Apple", "Orange", "Pear", "Watermelon", "Kiwi", "Strawberries", "Blueberries", "Raspberries", "Pomegranate", "Peaches", "Cantaloupe", "Honeydew", "Banana"] },
  { name: "Veggies", emoji: "🥦", group: "Vegetables", items: ["Lettuce", "Spinach", "Kale", "Broccoli", "Cabbage", "Peppers", "Asparagus", "Celery", "Tomatoes", "Carrots", "Radish", "Onions", "Garlic", "Mushroom", "Avocado"] },
  { name: "Drinks & Extras", emoji: "🍵", group: null, items: ["Lemon Water", "Lemons", "Limes", "Tea", "Coffee", "Stevia", "Olive Oil"] },
];

// Map each food → its rainbow color & food group (for auto-checking the rainbow)
const FOOD_COLOR = {
  Apple: "red", Strawberries: "red", Raspberries: "red", Pomegranate: "red", Watermelon: "red", Tomatoes: "red", Radish: "red", Peppers: "red",
  Orange: "orange", Carrots: "orange", Peaches: "orange", Cantaloupe: "orange",
  Banana: "yellow", Lemons: "yellow",
  Kiwi: "green", Pear: "green", Lettuce: "green", Spinach: "green", Kale: "green", Broccoli: "green", Asparagus: "green", Celery: "green", Avocado: "green", Honeydew: "green", Limes: "green",
  Blueberries: "blue",
  Cabbage: "purple", Onions: "purple",
  Mushroom: "white", Garlic: "white",
};
const FOOD_GROUP_OF = FOOD_LOG_GROUPS.reduce((acc, g) => { if (g.group) g.items.forEach((i) => { acc[i] = g.group; }); return acc; }, {});

const HOLISTIC_PILLARS = [
  { emoji: "🥗", title: "Nourish", desc: "Eat the rainbow daily — colorful whole foods, lean protein, and plenty of water. Food is fuel and self-love." },
  { emoji: "🚶‍♀️", title: "Move with joy", desc: "Gentle, consistent movement you enjoy — walks, stretching, strength. Progress over perfection." },
  { emoji: "🌙", title: "Rest & restore", desc: "Protect 7–9 hours of sleep and true rest. Your body heals and releases when it feels safe." },
  { emoji: "🙏", title: "Mind & faith", desc: "Renew your mind with scripture, prayer, and affirmations. Bloom from the inside out." },
  { emoji: "📈", title: "Track & reflect", desc: "Use your Weekly Weigh-In and Daily Ritual to notice patterns — and celebrate every win." },
  { emoji: "💗", title: "Supported care", desc: "When you need more, book a practitioner visit for personalized, clinically-guided support." },
];

function startOfWeek(d) {
  const dt = new Date(d);
  const day = (dt.getDay() + 6) % 7;
  dt.setDate(dt.getDate() - day);
  dt.setHours(0, 0, 0, 0);
  return dt;
}

export default function ShapeUp() {
  const [tab, setTab] = useState("plan");
  const [goal, setGoal] = useState(null);
  const [goalForm, setGoalForm] = useState({ start_weight: "", target_weight: "", weekly_goal: "1", unit: "lbs" });
  const [weights, setWeights] = useState([]);
  const [weighIn, setWeighIn] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);

  const [weekStart] = useState(startOfWeek(new Date()));
  const [activeDay, setActiveDay] = useState(new Date().toISOString().slice(0, 10));
  const [menu, setMenu] = useState({});
  const [newItem, setNewItem] = useState({});
  const [rainbow, setRainbow] = useState([]);
  const [foodCounts, setFoodCounts] = useState({});
  const [recipePicks, setRecipePicks] = useState([]);
  const [recipe, setRecipe] = useState("");
  const [recipeBusy, setRecipeBusy] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [savingFav, setSavingFav] = useState(false);
  const [openFav, setOpenFav] = useState(null);

  const loadGoal = () => api.get("/shapeup/goal").then((r) => {
    if (r.data && r.data.target_weight) {
      setGoal(r.data);
      setGoalForm({ start_weight: r.data.start_weight, target_weight: r.data.target_weight, weekly_goal: r.data.weekly_goal, unit: r.data.unit });
    }
  }).catch(() => {});
  const loadWeights = () => api.get("/shapeup/weights").then((r) => setWeights(r.data)).catch(() => {});
  const loadMenu = (day) => api.get(`/shapeup/menu?date=${day}`).then((r) => {
    const m = {}; r.data.forEach((e) => { m[e.slot] = e.items; }); setMenu(m);
  }).catch(() => {});
  const loadRainbow = (day) => api.get(`/shapeup/rainbow?date=${day}`).then((r) => setRainbow(r.data.checked || [])).catch(() => {});
  const loadFood = (day) => api.get(`/shapeup/foodlog?date=${day}`).then((r) => setFoodCounts(r.data.counts || {})).catch(() => {});
  const loadFavorites = () => api.get("/shapeup/favorites").then((r) => setFavorites(r.data || [])).catch(() => {});

  useEffect(() => { loadGoal(); loadWeights(); loadFavorites(); }, []);
  useEffect(() => { loadMenu(activeDay); loadRainbow(activeDay); loadFood(activeDay); }, [activeDay]);

  const changeFood = async (food, delta) => {
    const cur = foodCounts[food] || 0;
    if (food === "Banana" && delta > 0 && cur >= 1) {
      toast.error("Bananas are limited to ½ a day on ShapeUp 🍌");
      return;
    }
    const nextVal = Math.max(0, cur + delta);
    const next = { ...foodCounts };
    if (nextVal === 0) delete next[food]; else next[food] = nextVal;
    setFoodCounts(next);
    try { await api.post("/shapeup/foodlog", { date: activeDay, counts: next }); }
    catch { toast.error("Could not save"); }
    // Auto-check the rainbow color & food group when logging a food
    if (delta > 0) {
      const additions = [];
      if (FOOD_COLOR[food]) additions.push(`color:${FOOD_COLOR[food]}`);
      if (FOOD_GROUP_OF[food]) additions.push(`group:${FOOD_GROUP_OF[food]}`);
      const merged = [...new Set([...rainbow, ...additions])];
      if (merged.length !== rainbow.length) {
        setRainbow(merged);
        api.post("/shapeup/rainbow", { date: activeDay, checked: merged }).catch(() => {});
      }
    }
  };

  const toggleRainbow = async (key) => {
    const next = rainbow.includes(key) ? rainbow.filter((k) => k !== key) : [...rainbow, key];
    setRainbow(next);
    try { await api.post("/shapeup/rainbow", { date: activeDay, checked: next }); }
    catch { toast.error("Could not save"); }
  };

  const saveGoal = async () => {
    if (!goalForm.start_weight || !goalForm.target_weight) { toast.error("Enter your start and target weight"); return; }
    setSavingGoal(true);
    try {
      const { data } = await api.post("/shapeup/goal", {
        start_weight: parseFloat(goalForm.start_weight), target_weight: parseFloat(goalForm.target_weight),
        weekly_goal: parseFloat(goalForm.weekly_goal || 1), unit: goalForm.unit,
      });
      setGoal(data); toast.success("Your ShapeUp goal is set! 🌷");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save goal"); }
    finally { setSavingGoal(false); }
  };

  const logWeighIn = async () => {
    if (!weighIn) return;
    try {
      await api.post("/shapeup/weight", { date: new Date().toISOString().slice(0, 10), weight: parseFloat(weighIn) });
      setWeighIn(""); loadWeights(); toast.success("Weigh-in saved 🌷");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save weigh-in"); }
  };

  const addMenuItem = async (slot, value) => {
    const val = (value !== undefined ? value : (newItem[slot] || "")).trim();
    if (!val) return;
    const items = [...(menu[slot] || []), val];
    setMenu({ ...menu, [slot]: items });
    if (value === undefined) setNewItem({ ...newItem, [slot]: "" });
    try { await api.post("/shapeup/menu", { date: activeDay, slot, items }); }
    catch { toast.error("Could not save"); }
  };

  const removeMenuItem = async (slot, idx) => {
    const items = (menu[slot] || []).filter((_, i) => i !== idx);
    setMenu({ ...menu, [slot]: items });
    try { await api.post("/shapeup/menu", { date: activeDay, slot, items }); }
    catch { toast.error("Could not save"); }
  };

  const toggleRecipePick = (food) =>
    setRecipePicks((prev) => prev.includes(food) ? prev.filter((f) => f !== food) : [...prev, food]);

  const generateRecipe = async () => {
    if (recipePicks.length === 0) { toast.error("Pick a few ingredients first"); return; }
    setRecipeBusy(true); setRecipe("");
    try {
      const { data } = await api.post("/shapeup/recipe", { ingredients: recipePicks });
      setRecipe(data.recipe);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create a recipe"); }
    finally { setRecipeBusy(false); }
  };

  const recipeDishName = () => {
    const m = recipe.match(/Dish:\s*(.+)/i);
    return (m ? m[1] : "AI recipe").trim();
  };

  const saveRecipeToMenu = async (slot) => {
    const dish = recipeDishName();
    const items = [...(menu[slot] || []), dish];
    setMenu({ ...menu, [slot]: items });
    try {
      await api.post("/shapeup/menu", { date: activeDay, slot, items });
      toast.success(`Saved "${dish}" to ${slot} 🌷`);
    } catch { toast.error("Could not save to your menu"); }
  };

  const saveFavorite = async () => {
    if (!recipe) return;
    setSavingFav(true);
    try {
      const { data } = await api.post("/shapeup/favorites", { dish: recipeDishName(), recipe });
      setFavorites([data, ...favorites]);
      toast.success("Added to your favorites 💗");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save favorite"); }
    finally { setSavingFav(false); }
  };

  const deleteFavorite = async (id) => {
    setFavorites(favorites.filter((f) => f.id !== id));
    try { await api.delete(`/shapeup/favorites/${id}`); }
    catch { toast.error("Could not remove favorite"); loadFavorites(); }
  };

  const relogFavorite = async (fav, slot = "Dinner") => {
    const items = [...(menu[slot] || []), fav.dish];
    setMenu({ ...menu, [slot]: items });
    try {
      await api.post("/shapeup/menu", { date: activeDay, slot, items });
      toast.success(`"${fav.dish}" logged to ${slot} 🌷`);
    } catch { toast.error("Could not log to your menu"); }
  };

  const days = [...Array(7)].map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });

  const latest = weights.length ? weights[weights.length - 1].weight : (goal?.start_weight || null);
  const lost = goal && latest != null ? (goal.start_weight - latest) : 0;
  const totalToLose = goal ? (goal.start_weight - goal.target_weight) : 0;
  const pct = totalToLose > 0 ? Math.min(100, Math.max(0, Math.round((lost / totalToLose) * 100))) : 0;
  const toGo = goal && latest != null ? Math.max(0, (latest - goal.target_weight)).toFixed(1) : "—";
  const chartData = weights.map((w) => ({ date: w.date.slice(5), weight: w.weight }));

  // weekly weigh-in rows with change vs previous
  const weighRows = [...weights].reverse().map((w, i, arr) => {
    const prev = arr[i + 1];
    const change = prev ? +(w.weight - prev.weight).toFixed(1) : null;
    return { ...w, change };
  });

  const TABS = [
    { k: "plan", l: "Holistic Plan", icon: Sparkles },
    { k: "goals", l: "Goals", icon: Target },
    { k: "weighin", l: "Weekly Weigh-In", icon: Scale },
    { k: "foodlog", l: "Food Log", icon: Apple },
    { k: "menu", l: "Menu Builder", icon: UtensilsCrossed },
  ];

  return (
    <AppLayout>
      <div className="animate-fade-up">
        <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">ShapeUp · Weight Loss Planner</span>
        <h1 className="mt-2 font-serif text-5xl tracking-tight text-wine">Bloom into your goal</h1>
        <p className="mt-3 text-base text-[#6E5862] max-w-xl">Set your weekly goals, weigh in, and build nourishing menus — gentle self-tracking to support your journey.</p>
      </div>

      <div className="mt-8 flex gap-2.5 overflow-x-auto pb-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.k} data-testid={`shapeup-tab-${t.k}`} onClick={() => setTab(t.k)}
              className={`whitespace-nowrap inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-all ${tab === t.k ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}>
              <Icon className="h-4 w-4" strokeWidth={1.5} /> {t.l}
            </button>
          );
        })}
      </div>

      {/* HOLISTIC PLAN */}
      {tab === "plan" && (
        <div className="mt-8">
          <div className="relative overflow-hidden rounded-[2rem] bg-wine p-8 sm:p-10 text-white">
            <span className="text-xs uppercase tracking-[0.2em] text-white/70">ShapeUp · The BloomHer Way</span>
            <h2 className="mt-2 font-serif text-4xl sm:text-5xl tracking-tight">Holistic Weight Loss Plan</h2>
            <p className="mt-3 text-white/85 max-w-xl">A faith-centered, whole-woman approach to releasing weight gently and sustainably — body, mind, and spirit in harmony. Lose weight like melting butter, the BloomHer way. 🌷</p>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {HOLISTIC_PILLARS.map((p) => (
              <div key={p.title} data-testid={`pillar-${p.title.split(' ')[0]}`} className="bg-white rounded-3xl border border-taupe p-6">
                <div className="text-3xl">{p.emoji}</div>
                <h3 className="mt-3 font-serif text-2xl text-[#3A1230]">{p.title}</h3>
                <p className="mt-2 text-sm text-[#6E5862] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 bg-[#F7E9EF] rounded-3xl border border-gold/20 p-6 sm:p-8">
            <h3 className="font-serif text-2xl text-[#3A1230]">Your simple weekly rhythm</h3>
            <ol className="mt-4 space-y-3">
              {[
                "Set your weight goal in the Goals tab.",
                "Plan colorful meals in the Menu Builder & check off your rainbow daily.",
                "Weigh in once a week and celebrate every win.",
                "Anchor each morning with your Daily Ritual & a scripture.",
                "Book a practitioner visit whenever you'd like clinical support.",
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="mt-0.5 h-6 w-6 rounded-full bg-wine text-white flex items-center justify-center text-xs font-medium flex-shrink-0">{i + 1}</span>
                  <span className="text-base text-[#6E5862]">{step}</span>
                </li>
              ))}
            </ol>
            <div className="mt-6 flex flex-wrap gap-3">
              <button data-testid="plan-start-goal" onClick={() => setTab("goals")} className="inline-flex items-center justify-center rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                Set my goal
              </button>
              <button data-testid="plan-open-menu" onClick={() => setTab("menu")} className="inline-flex items-center justify-center rounded-full border border-wine px-6 py-3 text-sm font-medium text-wine hover:bg-wine/5 transition-all">
                Build my menu
              </button>
            </div>
          </div>
          <Disclaimer className="mt-8" />
        </div>
      )}

      {/* GOALS */}
      {tab === "goals" && (
        <div className="mt-8 grid lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-[2rem] border border-taupe p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><Target className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
              <h2 className="font-serif text-3xl text-[#3A1230]">Your weekly goal</h2>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#6E5862] mb-2">Starting weight</label>
                <input data-testid="goal-start" type="number" value={goalForm.start_weight} onChange={(e) => setGoalForm({ ...goalForm, start_weight: e.target.value })}
                  className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6E5862] mb-2">Target weight</label>
                <input data-testid="goal-target" type="number" value={goalForm.target_weight} onChange={(e) => setGoalForm({ ...goalForm, target_weight: e.target.value })}
                  className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" placeholder="0" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6E5862] mb-2">Weekly goal</label>
                <input data-testid="goal-weekly" type="number" step="0.5" value={goalForm.weekly_goal} onChange={(e) => setGoalForm({ ...goalForm, weekly_goal: e.target.value })}
                  className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" placeholder="1" />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#6E5862] mb-2">Unit</label>
                <select data-testid="goal-unit" value={goalForm.unit} onChange={(e) => setGoalForm({ ...goalForm, unit: e.target.value })}
                  className="w-full rounded-xl border border-[#D8C2CD] bg-cream px-4 py-3 focus:border-wine focus:outline-none">
                  <option value="lbs">lbs</option><option value="kg">kg</option>
                </select>
              </div>
            </div>
            <button data-testid="goal-save" onClick={saveGoal} disabled={savingGoal} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-wine px-6 py-3.5 text-sm font-medium text-white hover:bg-wine-dark disabled:opacity-60 transition-all">
              {savingGoal ? <Loader2 className="h-5 w-5 animate-spin" /> : <><Check className="h-4 w-4" /> Save goal</>}
            </button>
          </div>

          <div className="bg-wine rounded-[2rem] p-8 text-white flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-4"><TrendingDown className="h-5 w-5" strokeWidth={1.5} /><span className="text-xs uppercase tracking-[0.15em] text-white/70">Progress</span></div>
            {goal ? (
              <>
                <div className="flex items-end gap-8">
                  <div><p className="font-serif text-6xl">{lost > 0 ? lost.toFixed(1) : "0.0"}</p><p className="text-xs text-white/70 mt-1">{goal.unit} lost</p></div>
                  <div><p className="font-serif text-6xl">{toGo}</p><p className="text-xs text-white/70 mt-1">{goal.unit} to go</p></div>
                </div>
                <div className="mt-6 h-2.5 rounded-full bg-white/20 overflow-hidden">
                  <div data-testid="goal-progress-bar" className="h-full bg-white transition-all duration-700" style={{ width: `${pct}%` }} />
                </div>
                <p className="mt-2 text-sm text-white/80">{pct}% to your goal of {goal.target_weight} {goal.unit}</p>
              </>
            ) : (
              <p className="font-serif text-2xl text-white/90">Set your goal to begin tracking your beautiful progress.</p>
            )}
          </div>
        </div>
      )}

      {/* WEEKLY WEIGH-IN */}
      {tab === "weighin" && (
        <div className="mt-8 grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-[2rem] border border-taupe p-8">
              <div className="flex items-center gap-3 mb-5">
                <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><Scale className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
                <h2 className="font-serif text-3xl text-[#3A1230]">This week's weigh-in</h2>
              </div>
              <p className="text-sm text-[#6E5862] mb-4">Step on the scale once a week, same day, same time. Be gentle with yourself.</p>
              <div className="flex gap-2">
                <input data-testid="weighin-input" type="number" value={weighIn} onChange={(e) => setWeighIn(e.target.value)} placeholder={`Weight (${goal?.unit || "lbs"})`}
                  className="flex-1 rounded-full border border-[#D8C2CD] bg-cream px-5 py-3 focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
                <button data-testid="weighin-log-btn" onClick={logWeighIn} className="rounded-full bg-wine px-6 text-white hover:bg-wine-dark transition-colors"><Plus className="h-5 w-5" /></button>
              </div>
            </div>

            <div className="bg-white rounded-[2rem] border border-taupe p-8">
              <h3 className="font-serif text-2xl text-[#3A1230] mb-4">Your weigh-in history</h3>
              {weighRows.length === 0 && <p className="text-sm text-[#9B7E8D]">No weigh-ins yet. Log your first above.</p>}
              <div className="space-y-2.5">
                {weighRows.map((w) => {
                  const losing = w.change != null && w.change < 0;
                  return (
                    <div key={w.id || w.date} data-testid={`weighin-row-${w.date}`} className="flex items-center justify-between rounded-2xl bg-cream px-4 py-3 border border-taupe">
                      <span className="text-sm text-[#6E5862]">{new Date(w.date + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
                      <span className="font-serif text-xl text-[#3A1230]">{w.weight} {goal?.unit || "lbs"}</span>
                      {w.change != null ? (
                        <span className={`inline-flex items-center gap-1 text-xs font-medium rounded-full px-2.5 py-1 ${losing ? "bg-[#EAF0EC] text-[#4A6B53]" : "bg-[#F5EBEB] text-[#9B4444]"}`}>
                          {losing ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}{Math.abs(w.change)} {goal?.unit || "lbs"}
                        </span>
                      ) : <span className="text-xs text-[#9B7E8D]">Baseline</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[2rem] border border-taupe p-8">
            <h3 className="font-serif text-2xl text-[#3A1230] mb-4">Your trend</h3>
            {chartData.length > 1 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EBD9E1" vertical={false} />
                    <XAxis dataKey="date" stroke="#9B7E8D" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis domain={["auto", "auto"]} stroke="#9B7E8D" fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #EBD9E1", fontFamily: "Jost" }} />
                    {goal && <ReferenceLine y={goal.target_weight} stroke="#B45D7F" strokeDasharray="4 4" label={{ value: "Goal", fontSize: 10, fill: "#B45D7F" }} />}
                    <Line type="monotone" dataKey="weight" stroke="#6B2554" strokeWidth={2.5} dot={{ fill: "#6B2554", r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="text-sm text-[#9B7E8D]">Log at least two weigh-ins to see your trend.</p>
            )}
          </div>
        </div>
      )}

      {/* FOOD LOG */}
      {tab === "foodlog" && (
        <div className="mt-8">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {days.map((d) => {
              const ds = d.toISOString().slice(0, 10);
              const active = ds === activeDay;
              return (
                <button key={ds} data-testid={`food-day-${ds}`} onClick={() => setActiveDay(ds)}
                  className={`flex flex-col items-center rounded-2xl px-4 py-3 min-w-[64px] transition-all ${active ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}>
                  <span className="text-xs uppercase tracking-wide">{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                  <span className="font-serif text-2xl">{d.getDate()}</span>
                </button>
              );
            })}
          </div>

          <div className="mt-4 rounded-2xl bg-[#F7E9EF] border border-gold/20 px-5 py-3 text-sm text-[#6E5862]">
            🌷 ShapeUp is <strong className="text-[#3A1230] font-medium">carb-free</strong> — tap to log proteins, fruits, veggies & healthy fats. Drinks: lemon water, tea & coffee with stevia only. Cook with olive oil.
          </div>

          <div className="mt-6 space-y-6">
            {FOOD_LOG_GROUPS.map((g) => (
              <div key={g.name} data-testid={`food-group-${g.name.split(' ')[0]}`} className="bg-white rounded-3xl border border-taupe p-6">
                <h3 className="font-serif text-2xl text-[#3A1230] mb-4">{g.emoji} {g.name}</h3>
                <div className="flex flex-wrap gap-2.5">
                  {g.items.map((food) => {
                    const count = foodCounts[food] || 0;
                    return (
                      <button
                        key={food}
                        data-testid={`food-btn-${food.replace(/\s+/g, '')}`}
                        onClick={() => changeFood(food, 1)}
                        onContextMenu={(e) => { e.preventDefault(); changeFood(food, -1); }}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm transition-all ${count > 0 ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}
                      >
                        {food}
                        {count > 0 && (
                          <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-white/90 text-wine text-xs font-medium">{count}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Today's log summary */}
          <div className="mt-6 bg-white rounded-3xl border border-taupe p-6">
            <h3 className="font-serif text-2xl text-[#3A1230] mb-4">Today's log</h3>
            {Object.keys(foodCounts).length === 0 ? (
              <p className="text-sm text-[#9B7E8D]">Nothing logged yet — tap any food above to add it.</p>
            ) : (
              <div className="space-y-2.5">
                {Object.entries(foodCounts).map(([food, count]) => (
                  <div key={food} data-testid={`food-logged-${food.replace(/\s+/g, '')}`} className="flex items-center justify-between rounded-2xl bg-cream px-4 py-2.5 border border-taupe">
                    <span className="text-sm text-[#3A1230]">{food}</span>
                    <div className="flex items-center gap-3">
                      <button data-testid={`food-minus-${food.replace(/\s+/g, '')}`} onClick={() => changeFood(food, -1)} className="h-7 w-7 rounded-full bg-[#F6ECF0] text-wine flex items-center justify-center hover:bg-taupe"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="font-serif text-lg text-wine w-5 text-center">{count}</span>
                      <button data-testid={`food-plus-${food.replace(/\s+/g, '')}`} onClick={() => changeFood(food, 1)} className="h-7 w-7 rounded-full bg-wine text-white flex items-center justify-center hover:bg-wine-dark"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <Disclaimer className="mt-8" />
        </div>
      )}

      {/* MENU BUILDER */}
      {tab === "menu" && (
        <div className="mt-8">
          <div className="flex gap-2 overflow-x-auto pb-2">
            {days.map((d) => {
              const ds = d.toISOString().slice(0, 10);
              const active = ds === activeDay;
              return (
                <button key={ds} data-testid={`menu-day-${ds}`} onClick={() => setActiveDay(ds)}
                  className={`flex flex-col items-center rounded-2xl px-4 py-3 min-w-[64px] transition-all ${active ? "bg-wine text-white" : "bg-white border border-taupe text-[#6E5862] hover:bg-cream"}`}>
                  <span className="text-xs uppercase tracking-wide">{d.toLocaleDateString(undefined, { weekday: "short" })}</span>
                  <span className="font-serif text-2xl">{d.getDate()}</span>
                </button>
              );
            })}
          </div>

          {/* What shall I make today? — AI recipe builder */}
          <div data-testid="recipe-builder" className="mt-6 bg-white rounded-3xl border border-taupe p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><ChefHat className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
              <div>
                <h3 className="font-serif text-2xl text-[#3A1230]">What shall I make today?</h3>
                <p className="text-sm text-[#6E5862]">Tap what's in your fridge and I'll create a carb-free recipe for you.</p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              {FOOD_LOG_GROUPS.filter((g) => g.group).map((g) => (
                <div key={g.name}>
                  <p className="text-xs uppercase tracking-[0.12em] text-[#9B7E8D] mb-2">{g.emoji} {g.name}</p>
                  <div className="flex flex-wrap gap-2">
                    {(g.name === "Veggies" ? [...g.items, "Lemons", "Limes"] : g.items).map((food) => {
                      const on = recipePicks.includes(food);
                      return (
                        <button key={food} data-testid={`recipe-pick-${food.replace(/\s+/g, '')}`} onClick={() => toggleRecipePick(food)}
                          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-all ${on ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>
                          {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}{food}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button data-testid="recipe-generate" onClick={generateRecipe} disabled={recipeBusy}
                className="inline-flex items-center gap-2 rounded-full bg-wine px-6 py-3 text-sm font-medium text-white hover:bg-wine-dark disabled:opacity-60 transition-all">
                {recipeBusy ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating your recipe…</> : <><ChefHat className="h-4 w-4" /> Create my recipe</>}
              </button>
              {recipePicks.length > 0 && (
                <button data-testid="recipe-clear" onClick={() => { setRecipePicks([]); setRecipe(""); }} className="text-sm text-[#9B7E8D] hover:text-wine">Clear</button>
              )}
            </div>
            {recipe && (
              <div data-testid="recipe-result" className="mt-5 rounded-2xl bg-[#F7E9EF] border border-gold/20 p-6 whitespace-pre-wrap text-[15px] leading-relaxed text-[#3A1230]">
                {recipe}
              </div>
            )}
            {recipe && (
              <div data-testid="recipe-save-row" className="mt-4">
                <p className="text-xs uppercase tracking-[0.12em] text-[#9B7E8D] mb-2">Save this recipe to today's menu</p>
                <div className="flex flex-wrap gap-2">
                  {SLOTS.map((s) => (
                    <button key={s.key} data-testid={`recipe-save-${s.key}`} onClick={() => saveRecipeToMenu(s.key)}
                      className="inline-flex items-center gap-1.5 rounded-full bg-wine px-4 py-2 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                      {s.emoji} {s.key}
                    </button>
                  ))}
                  <button data-testid="recipe-favorite" onClick={saveFavorite} disabled={savingFav}
                    className="inline-flex items-center gap-1.5 rounded-full border border-wine px-4 py-2 text-sm font-medium text-wine hover:bg-[#F6ECF0] disabled:opacity-60 transition-all">
                    {savingFav ? <Loader2 className="h-4 w-4 animate-spin" /> : <Heart className="h-4 w-4" />} Save to favorites
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* My favorite recipes — re-log in one tap */}
          <div data-testid="favorites-section" className="mt-6 bg-white rounded-3xl border border-taupe p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center"><Heart className="h-6 w-6 text-wine" strokeWidth={1.5} /></div>
              <div>
                <h3 className="font-serif text-2xl text-[#3A1230]">My favorite recipes</h3>
                <p className="text-sm text-[#6E5862]">Your saved cookbook — re-log a loved meal to today's menu in one tap.</p>
              </div>
            </div>
            {favorites.length === 0 ? (
              <p data-testid="favorites-empty" className="mt-4 text-sm text-[#9B7E8D]">No favorites yet. Generate a recipe above and tap “Save to favorites”. 💗</p>
            ) : (
              <div className="mt-4 space-y-3">
                {favorites.map((fav) => (
                  <div key={fav.id} data-testid={`favorite-${fav.id}`} className="rounded-2xl border border-taupe bg-cream/40 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <button onClick={() => setOpenFav(openFav === fav.id ? null : fav.id)}
                        className="flex items-center gap-2 text-left font-serif text-lg text-[#3A1230]">
                        <ChevronDown className={`h-4 w-4 text-wine transition-transform ${openFav === fav.id ? "rotate-180" : ""}`} />
                        {fav.dish}
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid={`favorite-relog-${fav.id}`} onClick={() => relogFavorite(fav)}
                          className="inline-flex items-center gap-1.5 rounded-full bg-wine px-4 py-2 text-sm font-medium text-white hover:bg-wine-dark transition-all">
                          <Plus className="h-4 w-4" /> Re-log
                        </button>
                        <button data-testid={`favorite-delete-${fav.id}`} onClick={() => deleteFavorite(fav.id)}
                          className="h-9 w-9 rounded-full bg-[#F6ECF0] text-[#9B4444] flex items-center justify-center hover:bg-taupe transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {openFav === fav.id && (
                      <div className="mt-3 whitespace-pre-wrap text-[14px] leading-relaxed text-[#3A1230] border-t border-taupe pt-3">
                        {fav.recipe}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Eat the Rainbow tracker */}
          <div data-testid="rainbow-tracker" className="mt-6 bg-white rounded-3xl border border-taupe p-6 sm:p-8">
            <h3 className="font-serif text-2xl text-[#3A1230]">Eat the rainbow</h3>
            <p className="text-sm text-[#6E5862] mt-1 mb-2">Check off each color you nourished yourself with today.</p>
            <p data-testid="rainbow-progress" className="text-sm font-medium text-wine mb-4">
              You've eaten {RAINBOW.filter((c) => rainbow.includes(`color:${c.key}`)).length} of {RAINBOW.length} rainbow colors today 🌷
            </p>
            <div className="flex flex-wrap gap-4">
              {RAINBOW.map((c) => {
                const key = `color:${c.key}`;
                const on = rainbow.includes(key);
                return (
                  <button key={c.key} data-testid={`rainbow-color-${c.key}`} onClick={() => toggleRainbow(key)} className="flex flex-col items-center gap-1.5 group">
                    <span className="relative h-12 w-12 rounded-full border-2 flex items-center justify-center transition-all"
                      style={{ backgroundColor: c.hex, borderColor: on ? "#6B2554" : "rgba(0,0,0,0.08)", boxShadow: on ? "0 0 0 3px rgba(107,37,84,0.15)" : "none" }}>
                      {on && <Check className="h-5 w-5" strokeWidth={3} style={{ color: c.key === "white" || c.key === "yellow" ? "#3A1230" : "#fff" }} />}
                    </span>
                    <span className={`text-xs ${on ? "text-wine font-medium" : "text-[#9B7E8D]"}`}>{c.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 grid sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-[#9B7E8D] mb-3">Food groups</p>
                <div className="flex flex-wrap gap-2">
                  {FOOD_GROUPS.map((g) => {
                    const key = `group:${g}`;
                    const on = rainbow.includes(key);
                    return (
                      <button key={g} data-testid={`rainbow-group-${g}`} onClick={() => toggleRainbow(key)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-all ${on ? "bg-wine text-white" : "bg-[#F6ECF0] text-[#6E5862] hover:bg-taupe"}`}>
                        {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}{g}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.12em] text-[#9B7E8D] mb-3">Supplements</p>
                <div className="flex flex-wrap gap-2">
                  {SUPPLEMENTS.map((s) => {
                    const key = `supp:${s}`;
                    const on = rainbow.includes(key);
                    return (
                      <button key={s} data-testid={`rainbow-supp-${s.replace(/\s+/g, '')}`} onClick={() => toggleRainbow(key)}
                        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-all ${on ? "bg-gold text-white" : "bg-[#F7E9EF] text-wine hover:bg-taupe"}`}>
                        {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}{s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            {SLOTS.map((s) => (
              <div key={s.key} data-testid={`menu-slot-${s.key}`} className="bg-white rounded-3xl border border-taupe p-6">
                <h3 className="font-serif text-2xl text-[#3A1230] mb-4">{s.emoji} {s.key}</h3>
                <div className="space-y-2 mb-4">
                  {(menu[s.key] || []).length === 0 && <p className="text-sm text-[#9B7E8D]">Nothing planned yet.</p>}
                  {(menu[s.key] || []).map((item, i) => (
                    <div key={i} className="flex items-center justify-between rounded-xl bg-cream px-4 py-2.5 border border-taupe">
                      <span className="text-sm text-[#3A1230]">{item}</span>
                      <button data-testid={`menu-remove-${s.key}-${i}`} onClick={() => removeMenuItem(s.key, i)} className="text-[#9B7E8D] hover:text-[#9B4444]"><X className="h-4 w-4" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input data-testid={`menu-input-${s.key}`} value={newItem[s.key] || ""} onChange={(e) => setNewItem({ ...newItem, [s.key]: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") addMenuItem(s.key); }} placeholder="Add a meal…"
                    className="flex-1 rounded-full border border-[#D8C2CD] bg-cream px-4 py-2.5 text-sm focus:border-wine focus:outline-none focus:ring-1 focus:ring-wine" />
                  <button data-testid={`menu-add-${s.key}`} onClick={() => addMenuItem(s.key)} className="rounded-full bg-wine px-4 text-white hover:bg-wine-dark transition-colors"><Plus className="h-4 w-4" /></button>
                </div>
                {/* Quick-add meal ideas */}
                <div className="mt-4">
                  <div className="flex items-center gap-1.5 mb-2 text-xs uppercase tracking-[0.12em] text-[#9B7E8D]"><Sparkles className="h-3.5 w-3.5 text-gold" /> BloomHer ideas</div>
                  <div className="flex flex-wrap gap-2">
                    {MEAL_IDEAS[s.key].map((idea) => (
                      <button key={idea} data-testid={`meal-idea-${s.key}-${idea.slice(0,6).replace(/\W/g,'')}`} onClick={() => addMenuItem(s.key, idea)}
                        className="rounded-full bg-[#F7E9EF] px-3 py-1.5 text-xs text-wine hover:bg-wine hover:text-white transition-all">
                        + {idea}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Disclaimer className="mt-10" />
    </AppLayout>
  );
}
